import type React from 'react';
import { useCallback, useRef } from 'react';
import type { AnimatedSvgRef, TriggerType } from './types';

export function useAnimatedIconInteraction(
  trigger: TriggerType = 'hover',
  ref?: React.Ref<AnimatedSvgRef>,
) {
  const internalRef = useRef<AnimatedSvgRef>(null);
  const actualRef = (ref as React.RefObject<AnimatedSvgRef>) ?? internalRef;
  const isForwardRef = useRef(false);

  const handleClick = useCallback(() => {
    if (isForwardRef.current) {
      actualRef.current?.runAnimationBackward();
    } else {
      actualRef.current?.runAnimationForward();
    }
    isForwardRef.current = !isForwardRef.current;
  }, [actualRef]);

  const handleMouseEnter = useCallback(() => {
    actualRef.current?.runAnimationForward();
  }, [actualRef]);

  const handleMouseLeave = useCallback(() => {
    actualRef.current?.runAnimationBackward();
  }, [actualRef]);

  if (typeof ref === 'function' || (ref && typeof ref === 'object')) {
    return { actualRef: ref, isClickable: false };
  }

  const triggerProps =
    trigger === 'click'
      ? { onClick: handleClick }
      : { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave };

  return { actualRef, isClickable: trigger === 'click', ...triggerProps };
}
